import React, { useEffect, useState, useRef } from 'react'
import CampaignTable from './CampaignTable'
import { Plus, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CampaignFilter from './CampaignFilter';
import ProgressCampaign from './ProgressCampaign';
import { useDispatch, useSelector } from 'react-redux';
import { deleteCampaign, duplicateCampaign, getCampaigns, getDetailCampaign } from 'slices/thunk';
import DeleteModal from 'Common/DeleteModal';
import { toast } from 'react-toastify';
import { sortByLastModification } from 'helpers/utils/sortByLastModification';
import Loading from 'Common/Components/Loading';

const Campaign: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [data, setData] = useState<any[]>([]);
  const [isInProgress, setIsInProgress] = useState(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isView, setIsView] = useState<boolean>(false);
  const [eventData, setEventData] = useState<any>();
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [searchText, setSearchText] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const campaigns = useSelector((state: any) => state.Campaign.campaigns);
  const campaignDetail = useSelector((state: any) => state.Campaign.campaignDetail);
  const loading = useSelector((state: any) => state.Campaign.loading);
  const [currentCampaignId, setCurrentCampaignId] = useState<string | null>(null);

  useEffect(() => {
    const storedCampaignId = localStorage.getItem('currentEmailCampaignId');
    if (storedCampaignId) {
      setCurrentCampaignId(storedCampaignId);
    }
  }, [currentCampaignId])

  useEffect(() => {
    dispatch(getCampaigns())
  }, [dispatch])

  useEffect(() => {
    setData(sortByLastModification(campaigns))
  }, [campaigns])

  useEffect(() => {
    if (campaignDetail?.id) {
      setEventData(campaignDetail);
    }
  }, [campaignDetail])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);


  const keysToSearch = ['name', 'subject'];
  const filteredEventData = Array.isArray(campaigns)
    ? campaigns.filter((item: any) =>
      keysToSearch.some((key: any) =>
        item[key]?.toLowerCase().includes(searchText.toLowerCase())
      )
    )
    : [];

  const handleOnSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  }

  const handleStatusChange = (selectedOption: any) => {
    if (selectedOption.value === 'Hidden') {
      setData(campaigns);
    } else {
      const filteredCampaign = Array.isArray(campaigns)
        ? campaigns.filter((data: any) => data?.status === selectedOption.value)
        : [];
      setData(filteredCampaign);
    }
  };

  const handleUpdateDataClick = (ele: any) => {
    if (ele?.isCampaignAgent) {
      navigate(`/campaign/campaign-agent/${ele.id}/edit`, { replace: true });
      setTimeout(() => {
        console.log('Location after navigate:', window.location.pathname);
        if (window.location.pathname === '/campaign') {
          console.log('Navigation failed, trying window.location');
          window.location.href = `/campaign/campaign-agent/${ele.id}/edit`;
        }
      }, 100);

      return;
    }
    if (ele?.id) {
      dispatch(getDetailCampaign(ele.id));
      setIsInProgress(true)
      setIsEdit(true);
    }
  };

  const handleViewDate = (ele: any) => {
    if (ele?.isCampaignAgent) {
      navigate(`/campaign/campaign-agent/${ele.id}/view`, { replace: true });
      // Also try with window.location as fallback
      setTimeout(() => {
        console.log('Location after navigate:', window.location.pathname);
        if (window.location.pathname === '/campaign') {
          console.log('Navigation failed, trying window.location');
          window.location.href = `/campaign/campaign-agent/${ele.id}/view`;
        }
      }, 100);

      return;
    }
    dispatch(getDetailCampaign(ele.id));
    setIsEdit(true);
    setIsInProgress(true)
    setIsView(true);
  }

  const onClickDelete = (cell: any) => {
    setDeleteModal(true);
    if (cell?.id) {
      dispatch(getDetailCampaign(cell.id));
      setEventData(cell);
    }
  };

  const onClickDuplicate = (cell: any) => {
    if (cell?.id) {
      dispatch(duplicateCampaign(cell.id)).then(() => {
        toast.success("Campaign is duplicated successfully", { autoClose: 2000 });
      });
    }
  }

  const handleDelete = () => {
    if (campaignDetail?.id) {
      dispatch(deleteCampaign(campaignDetail.id))
        .then(() => {
          setDeleteModal(false);
          toast.success("Campaign Deleted Successfully", { autoClose: 2000 });
        });
    }
  };

  const handelProcessCreateTemplate = () => {
    setIsInProgress(true)
    setIsEdit(false);
    setEventData(null);
  };

  const handleCampaignAgent = () => {
    navigate('/campaign/campaign-agent');
    setDropdownOpen(false);
  };

  const handleStandardCampaign = () => {
    handelProcessCreateTemplate();
    setDropdownOpen(false);
  };
  const cancelProgress = () => {
    setIsView(false);
    setIsInProgress(false);
    setIsEdit(false);
    setEventData(null);
  };

  return (
    <>
      <DeleteModal
        title="Are you sure you want to proceed?"
        content='This action cannot be undone.'
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
        onDelete={handleDelete}
        cancelText='Cancel'
        deleteText='Delete' />

      {/* <CampaignTable /> */}
      <div className="w-full h-[calc(100vh-70px)] bg-white overflow-hidden">
        {!isInProgress &&
          <div className="p-2 sm:p-3 md:p-4 lg:p-5 !mb-0" id="usersTable">
            <div className="overflow-x-auto">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                <div className="w-full sm:w-auto">
                  <h6 className="text-15 mb-3">Campaign Management</h6>
                </div>
              </div>
            </div>
            <CampaignFilter onSearch={handleOnSearch} textValue={searchText} onClearSearch={() => { setSearchText(""); }} onStatusChange={handleStatusChange} />
            {loading
              ? <div className="mt-3 min-h-[100px]"><Loading size="sm" color="blue" isVisible={true} /></div>
              : <div>
                <div className='mt-2 flex items-center justify-center sm:justify-end me-0 sm:me-2'>
                  <div ref={dropdownRef} className="relative inline-block text-left">
                    <div className="flex">
                      {/* <button
                        onClick={handleStandardCampaign}
                        type="button"
                        className="text-xs sm:text-sm text-white btn bg-blue-500 hover:bg-blue-600 hover:text-white px-3 py-2 rounded-l-md border-r border-blue-400"
                      >
                        <Plus className="inline-block size-4" /> <span className="align-middle">Add Campaign</span>
                      </button> */}
                      <button
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        type="button"
                        className="text-xs sm:text-sm text-white btn bg-blue-500 hover:bg-blue-600 hover:text-white px-2 py-2 rounded-r-md border-l border-blue-400"
                      >
                        <Plus className="inline-block size-4" /> <span className="align-middle">Add Campaign</span> <ChevronDown className="inline-block size-4" />
                      </button>
                    </div>
                    {dropdownOpen && (
                      <div className="absolute right-0 mt-2 w-56 origin-top-right bg-white border border-gray-200 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                        <div className="py-1">
                          <button
                            onClick={handleStandardCampaign}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                          >
                            <Plus className="inline-block size-4 mr-2" />
                            Standard Campaign
                          </button>
                          <button
                            onClick={handleCampaignAgent}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                          >
                            <Plus className="inline-block size-4 mr-2" />
                            Campaign Agent
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <CampaignTable
                  campaigns={filteredEventData}
                  onEdit={handleUpdateDataClick}
                  onDelete={onClickDelete}
                  onView={handleViewDate}
                  onDuplicate={onClickDuplicate}
                  searchText={searchText}
                  campaignId={currentCampaignId}
                />
              </div>
            }
          </div>
        }

        {/* <ProgressCampaign /> */}
        {isInProgress && <ProgressCampaign isViewProgress={isView} cancelProgress={cancelProgress} isInProgress={isInProgress} isEditProgress={isEdit} eventData={eventData} ></ProgressCampaign>
        }
      </div >
    </>
  )
}

export default Campaign